import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Search, Stethoscope } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  getDiseaseRecommendations,
  searchDiseases,
  type DiseaseDTO,
  type RecommendationItemDTO,
} from '@/services/clinical';
import {
  CLINICAL_SUGGESTION_DISCLAIMER,
  buildPrescriptionSuggestionHtml,
  type TherapeuticSuggestionPayload,
} from '@/lib/therapeuticSuggestion';

interface TherapeuticSuggestionPanelProps {
  disabled?: boolean;
  onApply: (payload: TherapeuticSuggestionPayload, html: string) => void;
}

export const TherapeuticSuggestionPanel: React.FC<TherapeuticSuggestionPanelProps> = ({
  disabled = false,
  onApply,
}) => {
  const [query, setQuery] = useState('');
  const [diseases, setDiseases] = useState<DiseaseDTO[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedDisease, setSelectedDisease] = useState<DiseaseDTO | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendationItemDTO[]>([]);
  const [disclaimer, setDisclaimer] = useState(CLINICAL_SUGGESTION_DISCLAIMER);
  const [recsLoading, setRecsLoading] = useState(false);
  const [recsError, setRecsError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setDiseases([]);
      setSearchError(null);
      return;
    }
    try {
      setSearchLoading(true);
      setSearchError(null);
      const res = await searchDiseases(term, 1, 15);
      if (res.success && res.data) {
        setDiseases(res.data.items);
        if (res.data.items.length === 0) {
          setSearchError('Nenhuma doença encontrada');
        }
      } else {
        setDiseases([]);
        setSearchError(res.error || 'Erro ao buscar doenças');
      }
    } catch {
      setDiseases([]);
      setSearchError('Erro de conexão ao buscar doenças');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runSearch(query);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  const selectDisease = async (disease: DiseaseDTO) => {
    setSelectedDisease(disease);
    setQuery(disease.name);
    setDiseases([]);
    setSelectedIds(new Set());
    setRecommendations([]);
    setRecsError(null);
    try {
      setRecsLoading(true);
      const res = await getDiseaseRecommendations(disease.id);
      if (res.success && res.data) {
        setRecommendations(res.data.items);
        setDisclaimer(res.data.disclaimer || CLINICAL_SUGGESTION_DISCLAIMER);
        if (res.data.items.length === 0) {
          setRecsError('Sem recomendações ativas para esta doença');
        }
      } else {
        setRecsError(res.error || 'Erro ao carregar recomendações');
      }
    } catch {
      setRecsError('Erro de conexão ao carregar recomendações');
    } finally {
      setRecsLoading(false);
    }
  };

  const toggleMedication = (medicationId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(medicationId)) next.delete(medicationId);
      else next.add(medicationId);
      return next;
    });
  };

  const handleApply = () => {
    if (!selectedDisease) return;
    const items = recommendations.filter((r) => selectedIds.has(r.medicationId));
    if (items.length === 0) return;

    const payload: TherapeuticSuggestionPayload = {
      diseaseId: selectedDisease.id,
      diseaseName: selectedDisease.name,
      clinicalCode: selectedDisease.clinicalCode,
      disclaimer,
      items: items.map((item) => ({
        medicationId: item.medicationId,
        medicationLabel: item.medicationLabel,
        suggestedDose: item.suggestedDose,
        suggestedFrequency: item.suggestedFrequency,
        suggestedDurationDays: item.suggestedDurationDays,
        standardInstructions: item.standardInstructions,
        administrationRoute: item.administrationRoute,
      })),
    };

    onApply(payload, buildPrescriptionSuggestionHtml(payload));
  };

  return (
    <div className="rounded-md border border-teal-200 bg-teal-50/60 dark:bg-teal-950/20 dark:border-teal-900 p-3 flex flex-col gap-3">
      <div className="flex items-start gap-2">
        <Stethoscope className="w-4 h-4 mt-0.5 text-teal-700 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-teal-900 dark:text-teal-100">
            Apoio terapêutico (opcional)
          </p>
          <p className="text-xs text-teal-800/80 dark:text-teal-200/80 mt-0.5">
            {disclaimer}
          </p>
        </div>
      </div>

      <div className="relative">
        <label className="text-sm font-medium mb-1 block">Buscar doença</label>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (selectedDisease && e.target.value !== selectedDisease.name) {
                setSelectedDisease(null);
                setRecommendations([]);
                setSelectedIds(new Set());
              }
            }}
            placeholder="Ex.: hipertensão, enxaqueca, ITU..."
            className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm"
            disabled={disabled}
            autoComplete="off"
          />
          {searchLoading && (
            <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
        </div>

        {diseases.length > 0 && !selectedDisease && (
          <ul className="absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-md border bg-background shadow-md">
            {diseases.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                  onClick={() => void selectDisease(d)}
                  disabled={disabled}
                >
                  <span className="font-medium">{d.name}</span>
                  {d.clinicalCode ? (
                    <span className="text-muted-foreground ml-2 text-xs">{d.clinicalCode}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}
        {searchError && !selectedDisease && query.trim() && !searchLoading && (
          <p className="text-xs text-muted-foreground mt-1">{searchError}</p>
        )}
      </div>

      {selectedDisease && (
        <div className="flex flex-col gap-2">
          <p className="text-sm">
            Selecionada:{' '}
            <strong>{selectedDisease.name}</strong>
            {selectedDisease.clinicalCode ? ` (${selectedDisease.clinicalCode})` : ''}
          </p>

          {recsLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Carregando recomendações...
            </div>
          )}

          {recsError && !recsLoading && (
            <p className="text-sm text-amber-700 dark:text-amber-400">{recsError}</p>
          )}

          {!recsLoading && recommendations.length > 0 && (
            <ul className="flex flex-col gap-2 max-h-56 overflow-auto">
              {recommendations.map((item) => {
                const checked = selectedIds.has(item.medicationId);
                return (
                  <li
                    key={item.recommendationId}
                    className="flex gap-2 items-start rounded border bg-background p-2"
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={checked}
                      onChange={() => toggleMedication(item.medicationId)}
                      disabled={disabled}
                      id={`med-${item.medicationId}`}
                    />
                    <label htmlFor={`med-${item.medicationId}`} className="text-sm cursor-pointer min-w-0">
                      <span className="font-medium block">{item.medicationLabel}</span>
                      <span className="text-xs text-muted-foreground block">
                        {item.therapeuticLine ? `${item.therapeuticLine} · ` : ''}
                        {item.suggestedDose} · {item.suggestedFrequency} ·{' '}
                        {item.suggestedDurationDays} dia(s)
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start border-teal-600 text-teal-800 hover:bg-teal-100"
            disabled={disabled || selectedIds.size === 0}
            onClick={handleApply}
          >
            Aplicar na receita ({selectedIds.size})
          </Button>
        </div>
      )}
    </div>
  );
};
